import { useState, useCallback } from 'react';

interface DragInfo {
    draggedItemId: string | null;
    draggedItemType: 'file' | 'folder' | null;
    dragOverItemId: string | null;
    dragOverItemType: 'file' | 'folder' | null;
    isValidDropTarget: boolean;
}

interface MoveOperations {
    moveItem: (sourceId: string, targetId: string | null) => Promise<Response>;
    moveBulkItems?: (itemIds: string[], targetId: string | null) => Promise<Response[]>;
    beforeMove?: () => Promise<void>;
    afterMove?: () => Promise<void>;
}

export const useDragAndDrop = (
    moveOperations: MoveOperations,
    onSuccess: () => void,
    onError?: (error: string) => void
) => {
    const [dragInfo, setDragInfo] = useState<DragInfo>({
        draggedItemId: null,
        draggedItemType: null,
        dragOverItemId: null,
        dragOverItemType: null,
        isValidDropTarget: false
    });

    const handleDragStart = useCallback((
        e: React.DragEvent<HTMLTableRowElement>,
        itemId: string,
        itemType: 'file' | 'folder'
    ) => {
        e.dataTransfer.setData('text/plain', itemId);
        e.dataTransfer.setData('item-type', itemType);
        setDragInfo(prev => ({
            ...prev,
            draggedItemId: itemId,
            draggedItemType: itemType
        }));
    }, []);

    const handleDragEnd = useCallback((e: React.DragEvent<HTMLTableRowElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragInfo({
            draggedItemId: null,
            draggedItemType: null,
            dragOverItemId: null,
            dragOverItemType: null,
            isValidDropTarget: false
        });
    }, []);

    const handleDragOver = useCallback((
        e: React.DragEvent<HTMLTableRowElement>,
        itemId: string | null,
        itemType: 'file' | 'folder' | null
    ) => {
        e.preventDefault();
        e.stopPropagation();

        // Get the type of the item being dragged
        const draggedType = dragInfo.draggedItemType;

        // Only allow dropping files into folders
        const isValidTarget = itemType === 'folder' && draggedType === 'file';

        setDragInfo(prev => ({
            ...prev,
            dragOverItemId: itemId,
            dragOverItemType: itemType,
            isValidDropTarget: isValidTarget
        }));

        // Set the drop effect based on validity
        e.dataTransfer.dropEffect = isValidTarget ? 'move' : 'none';
    }, [dragInfo.draggedItemType]);

    const handleDrop = useCallback(async (
        e: React.DragEvent<HTMLTableRowElement>,
        targetId: string | null,
        targetType: 'file' | 'folder' | null,
        selectedFiles: Set<string>
    ) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const sourceId = e.dataTransfer.getData('text/plain');
            const sourceType = e.dataTransfer.getData('item-type') as 'file' | 'folder';

            // If not dropping on a folder or dropping on invalid target, just cleanup and return
            if (!targetType || targetType !== 'folder' || sourceType !== 'file' || sourceId === targetId) {
                // Don't reset drag state here as dragend will handle it
                return;
            }

            if (moveOperations.beforeMove) {
                await moveOperations.beforeMove();
            }

            if (selectedFiles.has(sourceId) && moveOperations.moveBulkItems) {
                const results = await moveOperations.moveBulkItems(Array.from(selectedFiles), targetId);
                if (results.some(res => !res.ok)) {
                    throw new Error('Some items could not be moved');
                }
            } else {
                const result = await moveOperations.moveItem(sourceId, targetId);
                if (!result.ok) {
                    throw new Error('Failed to move item');
                }
            }

            if (moveOperations.afterMove) {
                await moveOperations.afterMove();
            }

            onSuccess();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to move items';
            onError?.(errorMessage);
        }
        // Don't reset drag state here as dragend will handle it
    }, [moveOperations, onSuccess, onError]);

    const resetDragState = useCallback(() => {
        setDragInfo({
            draggedItemId: null,
            draggedItemType: null,
            dragOverItemId: null,
            dragOverItemType: null,
            isValidDropTarget: false
        });
    }, []);

    return {
        dragInfo,
        handleDragStart,
        handleDragOver,
        handleDrop,
        handleDragEnd,
        resetDragState
    };
};